import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  CustomCodeEditor,
  CustomInput,
  CustomSelect,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import PeriodSelector from '../Components/PeriodSelector';
import { ApiKeyApiProvider, useApiKeyApi } from '../Services/ApiKeyApiProvider';
import { isEmpty } from '../utils/validationUtil';

function ApiKeyContent({ canEdit }) {
  const { organizationId, apiKeyId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getApiKeyById, createApiKey, updateApiKey } = useApiKeyApi();

  const [name, setName] = useState('');
  const [api_key, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [customTo, setCustomTo] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('6_months');

  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const periods = [
    { label: '3 Years', value: '3_years' },
    { label: '1 Year', value: '1_year' },
    { label: '6 Months', value: '6_months' },
    { label: '1 Month', value: '1_month' },
    { label: 'Custom', value: 'custom' },
  ];

  const getDateRangeByPeriod = (period) => {
    const today = new Date();
    let expiresAt = null;

    switch (period) {
      case '3_years':
        expiresAt = new Date(today);
        expiresAt.setFullYear(today.getFullYear() + 3);
        break;
      case '1_year':
        expiresAt = new Date(today);
        expiresAt.setFullYear(today.getFullYear() + 1);
        break;
      case '6_months':
        expiresAt = new Date(today);
        expiresAt.setMonth(today.getMonth() + 6);
        break;
      case '1_month':
        expiresAt = new Date(today);
        expiresAt.setMonth(today.getMonth() + 1);
        break;
      default:
        expiresAt = null;
    }

    return {
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    };
  };

  useEffect(() => {
    const fetchApiKey = async () => {
      setLoading(true);
      try {
        console.log('Fetching API Key with ID:', apiKeyId);
        if (apiKeyId !== 'new-api-key') {
          const response = await getApiKeyById(apiKeyId);
          console.log('Fetched API Key:', response);
          if (response) {
            setName(response.name || '');
            setExpiresAt(response.expires_at || '');
            setApiKey(response.api_key || '');
            setPermissions(response.permissions || []);
            setIsNew(false);
            setIsDisabled(!canEdit);
            setIsActive(response.is_active);
          }
        } else {
          setIsNew(true);
          setIsDisabled(!canEdit);
          setName('');
          setExpiresAt('');
          setPermissions([]);
        }
      } catch {
        showAlert('fail', 'Failed to load API Key');
      } finally {
        setLoading(false);
      }
    };

    fetchApiKey();
  }, [apiKeyId, canEdit]);

  const onCancel = () => {
    navigate(-1);
  };

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Name is required');
      return;
    }

    let expiresAtValue;
    if (!isNew) {
      expiresAtValue = expiresAt;
    } else if (selectedPeriod === 'custom') {
      expiresAtValue = customTo;
    } else {
      expiresAtValue = getDateRangeByPeriod(selectedPeriod).expiresAt;
    }

    const data = {
      name,
      permissions,
      expires_at: expiresAtValue,
    };

    if (isNew) {
      showConfirmation(`Are you sure you want to create a new API Key?`, async () => {
        try {
          setLoading(true);
          const result = await createApiKey(data);
          console.log('API Key created successfully:', result);
          showAlert('success', 'API Key successfully created!');
          navigate(`/organizations/${organizationId}/api-key/${result.id}`);
        } catch (error) {
          showAlert('fail', 'Failed to create API Key');
        } finally {
          setLoading(false);
        }
      });
    } else {
      showConfirmation(`Are you sure you want to save the changes?`, async () => {
        try {
          setLoading(true);
          data.is_active = isActive;
          await updateApiKey(apiKeyId, data);
          showAlert('success', 'API Key successfully updated!');
        } catch (error) {
          showAlert('fail', 'Failed to update channel');
        } finally {
          setLoading(false);
        }
      });
    }
  };

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const formatExpiresAt = (isoString) => {
    if (!isoString) return { date: 'No expiration', relative: null };

    const expiresDate = new Date(isoString);
    const now = new Date();

    const date = expiresDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const isPast = expiresDate < now;
    const diffMs = Math.abs(expiresDate - now);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0 && years === 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

    const relative =
      parts.length > 0
        ? isPast
          ? `expired ${parts.join(' ')} ago`
          : `${parts.join(' ')} remaining`
        : isPast
          ? 'expired today'
          : 'expires today';

    return { date, relative, isPast };
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{name}</h2>

          <div className="input-block">
            <CustomInput
              id="api-key-name"
              label="Name"
              placeholder="Enter API Key Name"
              value={name}
              onChange={(id, val) => setName(val)}
              disabled={isDisabled}
            />
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id="permissions-select"
                label="Permissions"
                options={[
                  { value: 'all:assistant', label: 'Assistant' },
                  { value: 'all:channel', label: 'Channel' },
                ]}
                value={permissions}
                multiSelect={true}
                onChange={(items) => setPermissions(items)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block">
            <label>Expires At</label>
            {!isNew ? (
              (() => {
                const { date, relative, isPast } = formatExpiresAt(expiresAt);
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: '8px',
                      padding: '10px 14px',
                      border: `2px solid ${isPast ? '#f5c2c7' : '#c9c0e8'}`,
                      borderRadius: '8px',
                      background: 'transparent',
                      position: 'relative',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '-9px',
                        left: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        color: isPast ? '#c0392b' : '#7c6bb0',
                        background: 'var(--bg-yes, #fff)',
                        padding: '0 4px',
                        letterSpacing: '0.8px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {isPast ? 'Expired' : 'Active until'}
                    </span>

                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'var(--text-primary, #2d2d2d)',
                      }}
                    >
                      {date}
                    </span>

                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '3px 10px',
                        borderRadius: '20px',
                        background: isPast ? '#fdecea' : '#ede9f8',
                        color: isPast ? '#c0392b' : '#6a52b3',
                      }}
                    >
                      {relative}
                    </span>
                  </div>
                );
              })()
            ) : (
              <>
                <PeriodSelector
                  selectedPeriod={selectedPeriod}
                  onChange={handlePeriodChange}
                  periods={periods}
                />
                {selectedPeriod === 'custom' && (
                  <div className="input-group mt-2">
                    <input
                      type="datetime-local"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {!isNew && (
            <>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate Key</div>
                  <div className="small-font">
                    Set the key to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="api-key-is-active"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-block">
                <h2 className="h2-title">API Key</h2>
                <CustomCodeEditor
                  id="api-key"
                  value={api_key}
                  boxtitle=""
                  wrapText={true}
                  language="javascript"
                  editable={false}
                  height="auto"
                  highlightActiveLine={false}
                  lightTheme="githubLight"
                  darkTheme="dracula"
                  lineNumbers={false}
                  foldGutter={false}
                />
              </div>
            </>
          )}

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApiKey() {
  return (
    <ConfirmationProvider>
      <ApiKeyApiProvider>
        <ApiKeyContent canEdit={true} />
      </ApiKeyApiProvider>
    </ConfirmationProvider>
  );
}

export default ApiKey;
